import { useListContext, useTranslate } from "ra-core";
import { Link } from "react-router";
import { CreateButton } from "@/components/admin/create-button";
import { List } from "@/components/admin/list";
import { SearchInput } from "@/components/admin/search-input";
import { SelectInput } from "@/components/admin/select-input";

import { TopToolbar } from "../../atomic-crm/layout/TopToolbar";
import { ListEmptyState } from "../../atomic-crm/misc/EmptyState";
import { ListSkeleton } from "../../atomic-crm/misc/ListSkeleton";
import { findChoice, PROJECT_TYPES, STYLE_STATUSES } from "../common/constants";
import { StatusBadge } from "../common/components";
import type { Style } from "../types";
import { Swatches, useStyleFonts } from "./StylePreview";
import { resolveTokens } from "./tokens";

const filters = [
  <SearchInput key="q" source="name@ilike" alwaysOn />,
  <SelectInput
    key="status"
    source="status"
    choices={STYLE_STATUSES}
    emptyText="hub.common.all"
    alwaysOn
  />,
  <SelectInput
    key="type"
    source="project_types@cs"
    label="resources.styles.fields.project_types"
    choices={PROJECT_TYPES}
    parse={(value: string) => (value ? `{${value}}` : value)}
    format={(value?: string) => value?.replace(/[{}]/g, "") ?? ""}
    emptyText="hub.common.all"
    alwaysOn
  />,
];

export const StyleList = () => {
  const translate = useTranslate();
  return (
    <List
      resource="styles"
      title={translate("resources.styles.name", { smart_count: 2 })}
      filters={filters}
      sort={{ field: "updated_at", order: "DESC" }}
      perPage={100}
      pagination={false}
      actions={
        <TopToolbar>
          <CreateButton label="hub.styles.new" />
        </TopToolbar>
      }
    >
      <StyleGallery />
    </List>
  );
};

const StyleGallery = () => {
  const { data, isPending } = useListContext<Style>();
  if (isPending) return <ListSkeleton variant="grid" rows={8} />;
  if (!data?.length) return <ListEmptyState className="my-6" />;
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 [&>*]:min-w-0 [&>*>*]:min-w-0">
      {data.map((style) => (
        <StyleCard key={style.id} style={style} />
      ))}
    </div>
  );
};

const StyleCard = ({ style }: { style: Style }) => {
  const translate = useTranslate();
  const t = resolveTokens(style.tokens);
  useStyleFonts([t.fonts.heading]);
  return (
    <Link
      to={`/styles/${style.id}/show`}
      className="group overflow-hidden rounded-xl border bg-card transition-shadow hover:shadow-md"
    >
      {/* Specimen of the style: heading font, text color and button. */}
      <figure
        className="relative flex h-32 flex-col justify-between p-4"
        style={{ background: t.colors.background, color: t.colors.foreground }}
        aria-label={translate("crm.style_preview.label")}
      >
        <figcaption className="text-[10px] font-semibold uppercase tracking-wider opacity-60">
          {translate("crm.style_preview.label")}
        </figcaption>
        <span className="flex items-end justify-between gap-3">
          <span className="flex min-w-0 items-baseline gap-2">
            <span
              className="text-4xl font-semibold"
              style={{ fontFamily: `"${t.fonts.heading}", system-ui` }}
              aria-hidden
            >
              Aa
            </span>
            <span className="truncate text-xs opacity-70">
              {t.fonts.heading}
            </span>
          </span>
          <span
            className="shrink-0 px-3 py-1 text-xs font-medium"
            style={{
              background: t.colors.primary,
              color: t.colors.surface,
              borderRadius: t.radius,
            }}
          >
            {translate("crm.style_preview.button")}
          </span>
        </span>
      </figure>
      <Swatches tokens={style.tokens} className="h-3" />
      <div className="space-y-2 p-3">
        <div className="flex items-start justify-between gap-2">
          <p className="font-medium">{style.name}</p>
          <StatusBadge choice={findChoice(STYLE_STATUSES, style.status)} />
        </div>
        <div className="flex flex-wrap gap-1 text-xs text-muted-foreground">
          {style.project_types?.map((type) => (
            <span key={type} className="rounded bg-muted px-1.5 py-0.5">
              {translate(`hub.styles.project_types.${type}`)}
            </span>
          ))}
          {style.tags?.map((tag) => (
            <span key={tag} className="px-1 py-0.5">
              #{tag}
            </span>
          ))}
        </div>
      </div>
    </Link>
  );
};
